import { useEffect, RefObject } from 'react';
import { useProductMatrix } from '../contexts/ProductMatrixContext';

type UseScrollBehaviorOptions = {
  scrollContainerRef: RefObject<HTMLDivElement>;
  searchParams: URLSearchParams;
};

export function useScrollBehavior({
  scrollContainerRef,
  searchParams,
}: UseScrollBehaviorOptions): void {
  const {
    currentView,
    selectedPlatform,
    highlightedProduct,
    setHighlightedProduct,
    platformLoading,
    platformProducts,
    activeTab,
    setShowScrollToTop,
  } = useProductMatrix();

  // Highlight from URL ?highlight= param
  useEffect(() => {
    const highlightParam = searchParams.get('highlight');
    if (!highlightParam) return;
    setHighlightedProduct(highlightParam);
    const timeout = setTimeout(() => setHighlightedProduct(null), 3000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Scroll to & highlight a product row
  useEffect(() => {
    if (!highlightedProduct || platformLoading || platformProducts.length === 0) return;

    const decoded = decodeURIComponent(highlightedProduct);

    const tryScroll = (attempt = 1, maxAttempts = 6) => {
      const elements = document.querySelectorAll('[data-product-name]');
      let found = false;

      elements.forEach(el => {
        const name = el.getAttribute('data-product-name');
        if (name?.toLowerCase() === decoded.toLowerCase()) {
          found = true;
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('!bg-blue-100', 'dark:!bg-blue-900/50', 'ring-2', 'ring-blue-500', 'ring-opacity-75');
          setTimeout(() => {
            el.classList.remove('!bg-blue-100', 'dark:!bg-blue-900/50', 'ring-2', 'ring-blue-500', 'ring-opacity-75');
          }, 4000);
        }
      });

      if (!found && attempt < maxAttempts) {
        setTimeout(() => tryScroll(attempt + 1, maxAttempts), 300);
      }
    };

    const t = setTimeout(() => tryScroll(), 500);
    return () => clearTimeout(t);
  }, [highlightedProduct, platformLoading, platformProducts.length, activeTab]);

  // Scroll-to-top button visibility
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let ticking = false;
    const listener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setShowScrollToTop(container.scrollTop > 200);
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', listener);
    return () => container.removeEventListener('scroll', listener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, selectedPlatform]);
}
